<template>
  <div
    class="flex flex-col lg:flex-row gap-2 lg:gap-0 items-start lg:items-center justify-start lg:justify-end w-full py-2.5 px-3 text-sm font-light bg-white border border-b-4 border-slate-200 text-slate-500">
    <div class="flex gap-4 items-center justify-start">
      <div class="flex items-center gap-4">
        <p class="w-full">{{ currentPage }} from {{ numberOfPages }}</p>
        <SingleSelect
          v-model="itemsPerPageValue"
          :options="pageSizes"
          placeholder="Rows per Page"
          direction="up"
          expand-above
          @on-change="emitPageChange" />
      </div>
      <p>
        {{ $t('common.totalRecords') }} <span class="text-gray-700">{{ totalRecords }}</span>
      </p>
    </div>

    <div
      class="flex items-center lg:ml-auto gap-2.5 overflow-x-auto"
      :class="{ 'hidden': numberOfPages == 1 }">
      <BaseButton
        outline
        color="light"
        :disabled="currentPage == 1"
        :class="currentPage == 1 ? 'pointer-events-none' : ''"
        class="flex items-center font-light rounded-none text-slate-400 border-slate-300 h-9 hover:text-white"
        @click="onPageChange('previous')">
        <Icon icon="mdi:chevron-left" class="w-6 h-6" />
        <span class="max-md:hidden">{{ $t('common.previous') }}</span>
      </BaseButton>
      <BaseButton
        outline
        color="light"
        class="flex items-center rounded-none h-9 hover:text-white"
        :class="{ 'border-theme-500 text-theme-500 ': currentPage == 1 }"
        @click="onPageChange(1)"
        >1</BaseButton
      >
      <span v-if="currentPage >= 4 && numberOfPages > 5" class="text-xl text-slate-400"> ...</span>
      <BaseButton
        outline
        color="light"
        v-for="page in displayedPageNumbers"
        :key="page"
        class="flex items-center rounded-none h-9 hover:text-white"
        :class="{ 'border-theme-500 text-theme-500 ': currentPage == page }"
        @click="onPageChange(page)"
        >{{ page }}</BaseButton
      >
      <span
        v-if="currentPage < numberOfPages - 3 && numberOfPages > 5"
        class="text-xl text-slate-400">
        ...</span
      >
      <BaseButton
        v-if="numberOfPages > 4 && currentPage <= numberOfPages - 3"
        outline
        color="light"
        class="flex items-center rounded-none h-9 hover:text-white"
        :class="{ 'border-theme-500 text-theme-500 ': currentPage == numberOfPages }"
        @click="onPageNumberClick(numberOfPages)"
        >{{ numberOfPages }}</BaseButton
      >

      <BaseButton
        outline
        color="light"
        :disabled="currentPage == numberOfPages"
        :class="currentPage == numberOfPages ? 'pointer-events-none' : ''"
        class="flex items-center font-light rounded-none text-slate-400 border-slate-300 h-9 hover:text-white"
        @click="onPageChange('next')">
        <span class="max-md:hidden">{{ $t('common.next') }}</span>

        <Icon icon="mdi:chevron-right" class="w-6 h-6" />
      </BaseButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import SingleSelect from './SingleSelect.vue';
import BaseButton from './BaseButton.vue';
import { Icon } from '@iconify/vue';

import { computed, ref } from 'vue';

const props = withDefaults(
  defineProps<{
    numberOfItems: number;
    itemsPerPage?: number;
    totalRecords?: number;
  }>(),
  {
    itemsPerPage: 50,
  }
);

const currentPage = defineModel<number>({ required: true, default: 1 });

const pageSizes = [5, 10, 25, 50, 100];

const itemsPerPageValue = ref(props.itemsPerPage);

const computedItemsPerPage = computed(() => {
  const { numberOfItems } = props;
  const { value: itemsPerPageVal } = itemsPerPageValue;
  if (!itemsPerPageVal) {
    return numberOfItems;
  }
  return Math.min(numberOfItems, itemsPerPageVal);
});

const numberOfPages = computed(() => {
  return Math.ceil(props.numberOfItems / computedItemsPerPage.value);
});

const emit = defineEmits(['pageChanged']);

function onPageNumberClick(pageNo: number) {
  currentPage.value = pageNo;
  emitPageChange();
}

function onPageChange(action: 'next' | 'previous' | number) {
  if (action === 'next' && currentPage.value < numberOfPages.value) {
    currentPage.value++;
  } else if (action === 'previous' && currentPage.value > 1) {
    currentPage.value--;
  } else if (typeof action === 'number') {
    currentPage.value = action;
  }
  emitPageChange();
}

function emitPageChange() {
  const newPageTotal = props.numberOfItems / itemsPerPageValue.value;
  if (Math.ceil(newPageTotal) < currentPage.value) {
    currentPage.value = 1;
  }

  emit('pageChanged', {
    page: currentPage.value,
    itemsPerPage: computedItemsPerPage.value,
    startIndex: paginatedItemsIndices.value[0],
    endIndex: paginatedItemsIndices.value[1],
  });
}
const paginatedItemsIndices = computed(() => {
  const startIndex = (currentPage.value - 1) * computedItemsPerPage.value;
  const endIndex = startIndex + computedItemsPerPage.value;
  return [startIndex, endIndex];
});

const displayedPageNumbers = computed(() => {
  if (numberOfPages.value < 5) {
    return Array.from({ length: numberOfPages.value - 1 }, (_, i) => i + 2);
  } else if (currentPage.value < 3) {
    return [2, 3, 4];
  } else if (currentPage.value > numberOfPages.value - 3) {
    return [
      numberOfPages.value - 3,
      numberOfPages.value - 2,
      numberOfPages.value - 1,
      numberOfPages.value,
    ];
  } else {
    return [currentPage.value - 1, currentPage.value, currentPage.value + 1, currentPage.value + 2];
  }
});
</script>
